import React, { useState } from 'react';
import { Card, Button, Badge, Input } from '../ui';
import {
  MapPin,
  Search,
  TrendingUp,
  Clock,
  Fuel,
  RefreshCw,
  AlertTriangle,
  BarChart3,
  Navigation,
} from 'lucide-react';
import { useRouteAnalyticsSummary, useSimilarRoutes, useJourneyAnalytics } from '../../hooks/analytics-hooks';
import { LocationAutocomplete } from '../LocationAutocomplete';
import RouteStatsCard from './RouteStatsCard';
import JourneyComparison from './JourneyComparison';
import TollSequenceTimeline from './TollSequenceTimeline';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const RouteAnalyticsTab = () => {
  const [searchParams, setSearchParams] = useState({
    origin_lat: '',
    origin_long: '',
    dest_lat: '',
    dest_long: '',
    route_signature: '',
  });
  const [originLocation, setOriginLocation] = useState({
    address: '',
    coordinates: null,
    locationData: null,
  });
  const [destLocation, setDestLocation] = useState({
    address: '',
    coordinates: null,
    locationData: null,
  });
  const [selectedLegId, setSelectedLegId] = useState(null);
  const [searchMode, setSearchMode] = useState('location');

  const {
    data: routeAnalytics,
    isLoading: routeLoading,
    refetch: refetchRoute,
  } = useRouteAnalyticsSummary(searchParams, {
    enabled: false,
  });

  const {
    data: journeyAnalytics,
    isLoading: journeyLoading,
  } = useJourneyAnalytics(selectedLegId);

  const handleSearch = () => {
    if (searchMode === 'location') {
      if (originLocation.coordinates && destLocation.coordinates) {
        // OpenRouteService returns coordinates as [longitude, latitude]
        setSearchParams((prev) => ({
          ...prev,
          origin_lat: originLocation.coordinates[1],
          origin_long: originLocation.coordinates[0],
          dest_lat: destLocation.coordinates[1],
          dest_long: destLocation.coordinates[0],
        }));
        // Trigger refetch after state update
        setTimeout(() => refetchRoute(), 100);
      }
    } else if (searchMode === 'signature' && searchParams.route_signature) {
      refetchRoute();
    }
  };

  const handleInputChange = (field, value) => {
    setSearchParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOriginChange = (locationData) => {
    setOriginLocation(locationData);
  };

  const handleDestinationChange = (locationData) => {
    setDestLocation(locationData);
  };

  const prepareChartData = () => {
    if (!routeAnalytics?.data?.recent_journeys) return [];

    return routeAnalytics.data.recent_journeys.map((journey) => ({
      date: new Date(journey.journey_start_time).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
      }),
      duration: journey.journey_duration_hours,
      distance: journey.fasttag_calculated_distance_km,
      fuel: journey.estimated_fuel_consumed_liters,
    }));
  };

  return (
    <div className="space-y-6">
      {/* Search Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="w-6 h-6 mr-2 text-blue-600" />
            Route Analytics
          </h2>
          <div className="flex items-center space-x-2">
            <Button
              variant={searchMode === 'location' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setSearchMode('location')}
            >
              By Location
            </Button>
            <Button
              variant={searchMode === 'signature' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setSearchMode('signature')}
            >
              By Route ID
            </Button>
          </div>
        </div>

        {searchMode === 'location' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LocationAutocomplete
              value={originLocation.address}
              onChange={handleOriginChange}
              placeholder="Search origin location (e.g., Mumbai, Maharashtra)"
              label="Origin Location"
            />
            <LocationAutocomplete
              value={destLocation.address}
              onChange={handleDestinationChange}
              placeholder="Search destination location (e.g., Pune, Maharashtra)"
              label="Destination Location"
            />
          </div>
        ) : (
          <div className="max-w-md">
            <Input
              type="text"
              placeholder="Enter Route Signature (MD5 hash)"
              value={searchParams.route_signature}
              onChange={(e) => handleInputChange('route_signature', e.target.value)}
              label="Route Signature"
            />
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          {searchMode === 'location' && originLocation.coordinates && destLocation.coordinates && (
            <div className="text-xs text-gray-500 space-y-1">
              <p>
                <span className="font-medium">Origin:</span> {originLocation.locationData?.name || originLocation.address}
                <span className="text-gray-400 ml-2">
                  ({originLocation.coordinates[1].toFixed(4)}, {originLocation.coordinates[0].toFixed(4)})
                </span>
              </p>
              <p>
                <span className="font-medium">Destination:</span> {destLocation.locationData?.name || destLocation.address}
                <span className="text-gray-400 ml-2">
                  ({destLocation.coordinates[1].toFixed(4)}, {destLocation.coordinates[0].toFixed(4)})
                </span>
              </p>
            </div>
          )}
          <div className={searchMode === 'location' && originLocation.coordinates && destLocation.coordinates ? '' : 'ml-auto'}>
            <Button
              onClick={handleSearch}
              loading={routeLoading}
              disabled={searchMode === 'location' && (!originLocation.coordinates || !destLocation.coordinates)}
            >
              <Search className="w-4 h-4 mr-2" />
              Search Route Analytics
            </Button>
          </div>
        </div>
      </Card>

      {/* Results Section */}
      {routeAnalytics?.data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <RouteStatsCard
              title="Duration Statistics"
              stats={{
                duration: {
                  avg: routeAnalytics.data.summary.avg_duration_hours,
                  max: routeAnalytics.data.summary.max_duration_hours,
                  min: routeAnalytics.data.summary.min_duration_hours,
                  total_journeys: routeAnalytics.data.summary.total_journeys,
                },
              }}
              icon={Clock}
              loading={routeLoading}
            />

            <RouteStatsCard
              title="Distance Statistics"
              stats={{
                distance: {
                  avg: routeAnalytics.data.summary.avg_distance_km,
                  max: routeAnalytics.data.summary.max_distance_km,
                  min: routeAnalytics.data.summary.min_distance_km,
                  total_journeys: routeAnalytics.data.summary.total_journeys,
                },
              }}
              icon={MapPin}
              loading={routeLoading}
            />

            <RouteStatsCard
              title="Fuel Consumption"
              stats={{
                fuel: {
                  avg: routeAnalytics.data.summary.avg_fuel_liters,
                  max: routeAnalytics.data.summary.max_fuel_liters,
                  min: routeAnalytics.data.summary.min_fuel_liters,
                  total_journeys: routeAnalytics.data.summary.total_journeys,
                },
              }}
              icon={Fuel}
              loading={routeLoading}
            />
          </div>

          {/* Charts */}
          {routeAnalytics.data.recent_journeys &&
            routeAnalytics.data.recent_journeys.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Duration Trend */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Journey Duration Trend</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={prepareChartData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" style={{ fontSize: '12px' }} />
                      <YAxis style={{ fontSize: '12px' }} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="duration"
                        stroke="#3b82f6"
                        name="Duration (hrs)"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>

                {/* Distance & Fuel Comparison */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Distance vs Fuel Consumption
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={prepareChartData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" style={{ fontSize: '12px' }} />
                      <YAxis style={{ fontSize: '12px' }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="distance" fill="#8b5cf6" name="Distance (km)" />
                      <Bar dataKey="fuel" fill="#f59e0b" name="Fuel (L)" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            )}

          {/* Recent Journeys Table */}
          {routeAnalytics.data.recent_journeys &&
            routeAnalytics.data.recent_journeys.length > 0 && (
              <Card>
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Journeys</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Leg ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Vehicle
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Start Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Duration
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Distance
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Tolls
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {routeAnalytics.data.recent_journeys.map((journey) => (
                        <tr
                          key={journey.journey_id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => setSelectedLegId(journey.leg_id)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                            #{journey.leg_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {journey.vehicle_no || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(journey.journey_start_time).toLocaleString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {journey.journey_duration_hours?.toFixed(1)} hrs
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {Math.round(journey.fasttag_calculated_distance_km)} km
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant="outline" size="sm">
                              {journey.total_tolls_passed} tolls
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedLegId(journey.leg_id);
                              }}
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
        </>
      )}

      {/* Journey Details Section */}
      {selectedLegId && journeyAnalytics?.data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <JourneyComparison
            journey={journeyAnalytics.data.journey}
            comparison={journeyAnalytics.data.journey_vs_average}
            loading={journeyLoading}
          />
          <TollSequenceTimeline
            tollSequence={journeyAnalytics.data.toll_sequence}
            loading={journeyLoading}
          />
        </div>
      )}

      {/* Empty State */}
      {!routeAnalytics && !routeLoading && (
        <Card className="p-12 text-center">
          <Navigation className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Search for Route Analytics
          </h3>
          <p className="text-gray-500 mb-6">
            Search for origin and destination locations to view historical analytics and performance metrics
          </p>
          <div className="max-w-md mx-auto text-left bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900 font-medium mb-2">Example searches:</p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Origin: Mumbai, Maharashtra → Destination: Pune, Maharashtra</li>
              <li>• Origin: Delhi → Destination: Jaipur, Rajasthan</li>
              <li>• Origin: Chennai Port → Destination: Bangalore</li>
            </ul>
          </div>
        </Card>
      )}

      {/* No Data State */}
      {routeAnalytics && !routeAnalytics.data?.summary && (
        <Card className="p-12 text-center">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Analytics Data Found</h3>
          <p className="text-gray-500">
            No historical journey data available for this route. Analytics will be available once
            journeys are processed.
          </p>
        </Card>
      )}
    </div>
  );
};

export default RouteAnalyticsTab;
